import React, {
  useEffect, useState,
} from 'react';

import { Typography } from '@mui/material';
import {
  Navigate, useNavigate, useParams,
} from 'react-router';

import Button from '../../components/Button';
import CommitForm from '../../components/CommitForm';
import ContentWrapper from '../../components/ContentWrapper';
import { LoadingWrapper } from '../../components/PageLayout';
import ProfileIcon from '../../components/ProfileIcon';
import ShareModal from '../../components/ShareModal';
import UsersList from '../../components/UsersList';
import {
  useGetGroupsQuery,
  useGetPermissionQuery,
  useSharePermissionMutation,
  useUnsharePermissionMutation,
  useUpdatePermissionMutation,
} from '../../services/permissions';
import useError from '../../utils/react/hooks/useError';
import useSearchStr from '../../utils/react/hooks/useSearchStr';
import useSuccess from '../../utils/react/hooks/useSuccess';
import useUsersQuery, { User } from '../../utils/react/hooks/useUsersQuery';

import {
  GroupChip,
  UserField,
  UserName,
} from './index.styles';

interface PermissionsPageContentProps {
  tag: string,
}

const PermissionsPageContent = ({ tag }: PermissionsPageContentProps) => {
  const navigate = useNavigate();

  // Search for users
  const [usersSearchStr, setUsersSearchStr] = useSearchStr('');

  // Permission query
  const { data: permissionData, isLoading } = useGetPermissionQuery({ tag });

  // Get list of all users and group names to look up labels
  const { data: allGroups, isLoading: isAllGroupsLoading } = useGetGroupsQuery({});
  const { data: allUsers, isLoading: isAllUsersLoading } = useUsersQuery({});

  // Permission update mutations
  const [updatePermission,
    {
      isLoading: isUpdateLoading,
      isSuccess: isUpdateSuccess,
      error: updateError,
    }] = useUpdatePermissionMutation();

  // Groups and users in displayed lists
  const [groups, setGroups] = useState<{
    name: string,
    groupId: string,
    id: string
  }[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  useEffect(() => {
    if (permissionData?.data.groups) {
      setGroups((allGroups?.data || []).filter((g) => permissionData.data.groups.includes(g.id)));
    }
  }, [allGroups, permissionData]);
  useEffect(() => {
    if (permissionData?.data.users && allUsers) {
      const userIds = permissionData.data.users.map((u) => u.value);
      const userDetails = (allUsers || []).filter((u) => userIds.includes(u.id));
      setUsers((permissionData?.data.users || []).map((u) => ({
        ...u,
        ...userDetails.find((d) => d.id === u.value),
        // Local filter as the users attached to permissions do not have a separate query endpoint
      } as User)).filter((u) => u.name.toLowerCase().includes(usersSearchStr.toLowerCase())));
    }
  }, [allUsers, permissionData, usersSearchStr]);

  // ShareModal Search
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [search, setSearch] = useState<string>('');
  const { data: searchRes, isFetching: isSearchResFetching } = useUsersQuery(
    { search }, { skip: search === '' || !shareModalOpen },
  );

  // Share / unshare permissions
  const [share, {
    isLoading: isShareLoading,
    error: shareError,
  }] = useSharePermissionMutation();
  const [unshare, {
    isLoading: isUnshareLoading,
    isSuccess: isUnshareSuccess,
    error: unshareError,
  }] = useUnsharePermissionMutation();

  const [unshareTarget, setUnshareTarget] = useState(null);

  // Update page title
  useEffect(() => {
    if (permissionData?.data?.label) {
      document.title = `${permissionData.data.label} | Cognito Permissions`;
    }
  }, [permissionData]);

  useSuccess(isUpdateSuccess, 'Permission updated');
  useSuccess(isUnshareSuccess, 'Permission unshared');
  useError(!!updateError, updateError);

  return (
    <>
      <LoadingWrapper loading={isLoading || isAllGroupsLoading || isAllUsersLoading}>
        <ContentWrapper
          title="Permission Details"
          backButtonHref={-1}
        >
          <CommitForm
            data={permissionData?.data}
            metadata={permissionData?.metadata}
            fields={{
              value: {
                xs: 12,
                md: 12,
              },
              label: {
                xs: 12,
                md: 12,
              },
            }}
            onSubmit={async (values) => updatePermission({ tag, newAttributes: values })}
            isSubmitLoading={isUpdateLoading}
            autoSubmit
          />
        </ContentWrapper>
        <ContentWrapper
          title="Groups"
        >
          {groups.length > 0 ? (
            <>
              {groups.map((group) => (
                <GroupChip
                  key={group.id}
                  label={group.name}
                  clickable
                  onClick={() => navigate(`/admin/groups?id=${group.id}`)}
                />
              ))}
            </>
          ) : (
            <Typography variant="caption">No Groups</Typography>
          )}
        </ContentWrapper>
        <ContentWrapper
          title="Users"
        >
          <UsersList
            data={users || []}
            onSearch={(s) => setUsersSearchStr(s)}
            columns={[{
              field: 'name',
              headerName: 'Name',
              width: 240,
              renderCell: ({ row }: any) => (
                <UserField>
                  <ProfileIcon
                    firstName={row.firstName}
                    lastName={row.lastName}
                    imageUrl={row.profileIcon?.image}
                    color={row.profileIcon?.color}
                    size={32}
                  />
                  <UserName>{row?.name}</UserName>
                </UserField>
              ),
            },
            {
              field: 'control',
              headerName: 'Control',
              width: 100,
              renderCell: ({ row }: any) => (
                <Button
                  size="small"
                  color="error"
                  variant="text"
                  allCaps
                  disabled={isUnshareLoading}
                  loading={isUnshareLoading && unshareTarget === row.id}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    setUnshareTarget(row.id);
                    unshare({ tag, userId: row.id });
                  }}
                >
                  Unshare
                </Button>
              ),
            }]}
            loading={isLoading}
            getRowHref={(u) => `/admin/${u.type === 'STUDENT' ? 'student' : 'staff'}/${u.id}`}
            onAddClick={() => setShareModalOpen(true)}
            addButtonLabel="Share"
          />
        </ContentWrapper>
      </LoadingWrapper>
      <ShareModal
        subject={permissionData?.data.label || 'Permission'}
        open={shareModalOpen}
        handleClose={() => setShareModalOpen(false)}
        searchUsers={(searchRes || []).filter((x) => !(permissionData?.data.users || [])
          .map((u) => u.value).includes(x?.id))}
        isSearchFetching={isSearchResFetching}
        users={users || []}
        search={(str) => setSearch(str)}
        share={(userId) => share({
          userId, tag,
        })}
        unshare={(userId) => unshare({
          userId, tag,
        })}
        isUnshareLoading={isUnshareLoading}
        isShareLoading={isShareLoading}
        error={shareError || unshareError}
      />
    </>
  );
};

const AdminPermissionsPage = () => {
  const { tag } = useParams();

  if (tag) {
    return (
      <PermissionsPageContent
        tag={tag}
      />
    );
  }

  // If no permissionTag is provided, return to Permissions page
  return <Navigate replace to="/admin/permissions" />;
};
export default AdminPermissionsPage;
